/**
 * Undo / redo for a design session.
 *
 * A plain value history: `past` / `present` / `future`, where the value is the
 * item array of the draft being edited. Because every layout mutation in
 * `flowLayout` and `gridLayout` returns a new array (or the original one when a
 * change is refused), pushing is a reference comparison and a refused edit
 * never lands on the stack.
 *
 * The history is session-scoped and is discarded with the draft. Nothing here is
 * persisted: undo moves through edits the user made in this sitting, not through
 * previously saved layouts.
 */

/** Deep enough for a design sitting, shallow enough to stay cheap to hold. */
export const HISTORY_LIMIT = 50;

export function createHistory(present) {
  return { past: [], present, future: [] };
}

/**
 * Records a new value. An unchanged value is ignored, so a no-op gesture — a
 * nudge into a wall, a drag that lands where it started — does not consume an
 * undo step.
 */
export function pushHistory(history, present) {
  if (!history) return createHistory(present);
  if (history.present === present) return history;
  const past = [...history.past, history.present];
  return {
    past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
    present,
    future: [],
  };
}

export function canUndo(history) {
  return Boolean(history?.past?.length);
}

export function canRedo(history) {
  return Boolean(history?.future?.length);
}

export function undoHistory(history) {
  if (!canUndo(history)) return history;
  const past = [...history.past];
  const present = past.pop();
  return { past, present, future: [history.present, ...history.future] };
}

export function redoHistory(history) {
  if (!canRedo(history)) return history;
  const [present, ...future] = history.future;
  return { past: [...history.past, history.present], present, future };
}

/** Replaces the value and clears both stacks — Reset is not an undoable edit. */
export function resetHistory(present) {
  return createHistory(present);
}
